import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faUser,
  faArrowRightArrowLeft,
} from '@fortawesome/free-solid-svg-icons';

// Actions
import { gameActions, modalActions } from '../actions';

const PlayersManager = ({ roleLabels = [], disabled = false }) => {
  // State
  const [item, setItem] = useState({});

  // Redux
  const game = useSelector((state) => state.game);

  // Hooks
  const dispatch = useDispatch();

  // Effect: Component initialisation
  useEffect(() => {
    if (game?.data) {
      setItem(game.data);
    }
  }, [game?.data]);

  // Methods
  const handleGroupChange = (email, e) => {
    // console.log(email, e.target.value);

    const gindex = parseInt(e.target.value);

    // Update the player's group
    dispatch(
      gameActions.updateRegisteredPlayer({
        _id: item._id,
        email,
        gindex,
      })
    );
  };

  const handleRoleChange = (rp) => {
    const { email, rindex } = rp;
    const newRindex = rindex === 0 ? 1 : 0;
    dispatch(
      gameActions.setPlayerRindex(item._id, { email, rindex: newRindex })
    );
  };

  const removePlayerModal = (e, user) => {
    const username = `${user.name} ${user.lastname} ${user.email}`.trim();

    dispatch(
      modalActions.open({
        title: 'Remove Player',
        body: (
          <div>
            <p>
              Are you sure you want to remove <b>{username}</b> from this game?
            </p>
          </div>
        ),
        buttons: [
          <button
            key='btn-2'
            className='btn btn-outline-secondary'
            onClick={() => dispatch(modalActions.close())}>
            Cancel
          </button>,
          <button
            key='btn-1'
            className='btn btn-danger'
            onClick={() => {
              dispatch(gameActions.removeRegistered(item._id, [user?.email]));
              dispatch(modalActions.close());
            }}>
            Remove
          </button>,
        ],
      })
    );
  };

  // Reset players and groups confirmation modal
  const resetPlayersModal = () => {
    dispatch(
      modalActions.open({
        title: 'Reset Players and Groups',
        body: (
          <div>
            <p>
              Are you sure you want to reset all players and groups for this
              game?
            </p>
          </div>
        ),
        buttons: [
          <button
            key='btn-2'
            className='btn btn-outline-secondary'
            onClick={() => dispatch(modalActions.close())}>
            Cancel
          </button>,
          <button
            key='btn-1'
            className='btn btn-danger'
            onClick={() => {
              dispatch(gameActions.resetRegisteredPlayers(item));
              dispatch(modalActions.close());
            }}>
            Reset
          </button>,
        ],
      })
    );
  };

  // Helper function:
  // If no gindex is provided, find the group the player belongs to
  // and get the index of that group, use that index+1 as gindex
  const fillGindexFromGroups = (p, groupsArray) => {
    if (typeof p.gindex === 'undefined') {
      const groupIndex = groupsArray.findIndex((g) =>
        g.players.find((gp) => gp.email === p.email)
      );
      p.gindex = groupIndex !== -1 ? groupIndex + 1 : null;
    }
    return p;
  };

  // Helper function:
  // If no rindex is provided on the registered player,
  // find the player and get the rindex from the player element
  const fillRindexFromPlayers = (p, playersArray) => {
    if (typeof p.rindex === 'undefined') {
      const playerIndex = playersArray.findIndex((gp) => gp.email === p.email);
      p.rindex = playerIndex !== -1 ? playersArray[playerIndex].rindex : null;
    }
    return p;
  };

  // Logic
  const ppg = item?.ppg || 1;

  // Let's use item.pgg to calculate the number of potential groups
  const fullGroups = item.registered?.length % item?.ppg === 0 ? true : false;
  const numGroups = item.registered
    ? Math.ceil(item.registered?.length / item?.ppg) + (fullGroups ? 1 : 0)
    : 0;

  // Let's also collect the groupes that are currently specified in the registered players gindex
  const currentGroups =
    item.registered
      ?.filter((rp) => typeof rp.gindex !== 'undefined')
      ?.map((rp) => rp.gindex) ?? [];
  const totalGroupsArray = [...Array(numGroups).keys()]?.map((g) => g + 1);
  const totalGroups = [...totalGroupsArray, ...currentGroups].sort();
  const uniqueGroupsIds = [...new Set(totalGroups)];

  // Sort registered players by group
  const registeredPlayers = item.registered
    ? [...item.registered]
        .map((r) => fillGindexFromGroups(r, item?.groups)) // Fill gindex if not set
        .map((r) => fillRindexFromPlayers(r, item?.players)) // Fill rindex if not set
        .sort((a, b) => {
          if (a.gindex < b.gindex) return -1;
          if (a.gindex > b.gindex) return 1;
          return 0;
        })
    : [];

  // console.log({ game, item, registeredPlayers });

  return (
    <div>
      <ul className='list-group'>
        {registeredPlayers.map((rp) => {
          const bgClass =
            rp.gindex % 2 === 0 ? 'bg-white' : 'bg-light bg-opacity-10';

          const thisGroupPlayers = registeredPlayers.filter(
            (rp2) => rp2.gindex === rp.gindex
          );
          const isGroupFull = thisGroupPlayers.length >= ppg;

          return (
            <li
              key={`li-${rp.email}`}
              className={`list-group-item d-flex align-items-center justify-content-between ${bgClass}`}>
              <small className='w-50'>
                {rp.email}{' '}
                <button
                  title='Remove this player from the game'
                  className='btn btn-sm btn-link text-danger small ms-1 lh-1'
                  disabled={disabled}
                  onClick={(e) => removePlayerModal(e, rp)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
                <br />
                {/* Full name */}
                <span className='text-secondary'>
                  {rp?.name} {rp?.lastname}
                </span>
                {/* Display role label or rindex */}
                <span className='text-secondary ms-4'>
                  Role: {roleLabels?.[rp?.rindex] || '-'}
                </span>
                {roleLabels?.[rp?.rindex]?.length > 0 ? (
                  <button
                    title="Toggle this player's role"
                    className='btn btn-sm btn-link text-secondary py-0 px-0 small ms-2 lh-1'
                    disabled={disabled}
                    onClick={() => handleRoleChange(rp)}>
                    <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                  </button>
                ) : null}
              </small>

              <small
                className={`small ms-auto me-3 fst-italic opacity-50 ${
                  isGroupFull ? 'text-success' : 'text-dark'
                }`}>
                <FontAwesomeIcon icon={faUser} className='me-1' />x{' '}
                {thisGroupPlayers.length}
              </small>

              <span className='word-break'>
                <select
                  key={`sel-${rp.email}-${rp.gindex}`}
                  className='form-select form-select-sm'
                  disabled={disabled}
                  defaultValue={rp?.gindex}
                  onChange={(e) => handleGroupChange(rp.email, e)}>
                  {uniqueGroupsIds.map((g) => (
                    <option key={`opt-${g}`} value={g}>
                      Group {g}
                    </option>
                  ))}
                </select>
              </span>
            </li>
          );
        })}
      </ul>
      {registeredPlayers?.length > 0 && (
        <button
          className='btn btn-sm btn-link mt-2'
          disabled={disabled}
          onClick={resetPlayersModal}>
          Reset Players and Groups
        </button>
      )}
    </div>
  );
};

export default PlayersManager;
